import { clockTime, pointTime } from '../../../shared/lib/format';
import { useCopy } from '../../../shared/lib/use-copy';
import { Button } from '../../../shared/ui/button';
import { CopyIcon } from '../../../shared/ui/icons';
import { SectionPanel } from '../../../shared/ui/section-panel';
import type { TSourceCustomer } from '../../../entities/customer';

interface IClientTrafficCardProps {
  row: TSourceCustomer;
  withSecret: boolean;
  now: number;
  timeZone?: string;
}

// Only the rolling count the API keeps per client; the by-day chart lands here once it has a series.
export const ClientTrafficCard = ({ row, withSecret, now, timeZone }: IClientTrafficCardProps) => {
  const { copied, copy } = useCopy();
  const { customer } = row;
  const { activity } = customer;

  if (!activity.lastRequestAt) {
    return (
      <SectionPanel title="Traffic" note={customer.name}>
        <div className="flex flex-col items-center gap-2 px-card py-5 text-center">
          <p className="text-data text-primary">Nothing has arrived yet</p>
          <p className="max-w-prose-line text-meta text-muted">
            Send {customer.name} their address{withSecret ? ' and secret' : ''}. The first request
            shows up here — not a row of zeros until then.
          </p>
          <Button size="sm" variant="secondary" onClick={() => void copy(row.ingestUrl)}>
            <CopyIcon size={12} /> {copied ? 'Copied' : 'Copy their address'}
          </Button>
        </div>
      </SectionPanel>
    );
  }

  const n = activity.requestsInWindow;
  return (
    <SectionPanel
      title="Traffic"
      note={activity.computedAt ? `as of ${clockTime(activity.computedAt, timeZone)}` : undefined}
    >
      <div className="flex flex-col gap-1 p-card">
        {activity.computedAt === null ? (
          <p className="text-data text-muted">Requests here are not counted yet.</p>
        ) : (
          <p className="flex items-baseline gap-3">
            <span className="text-display font-medium tracking-display text-primary">
              {n.toLocaleString('en-US')}
            </span>
            <span className="text-data text-muted">
              request{n === 1 ? '' : 's'} in the past {activity.windowHours} h
            </span>
          </p>
        )}
        <p className="font-mono text-micro text-faint">
          last request {pointTime(activity.lastRequestAt, now, timeZone)}
        </p>
      </div>
    </SectionPanel>
  );
};
